import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { ShieldCheck, Loader2, Check, X, RotateCcw, AlertTriangle, User, Link2Off, History } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const [items, setItems] = useState(null);
  const [grants, setGrants] = useState(null);
  const [tab, setTab] = useState("PENDING");
  const [busy, setBusy] = useState(false);
  const [expiryDays, setExpiryDays] = useState({});
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const load = useCallback(() => api.approvals(tab).then(setItems), [tab]);
  const loadGrants = useCallback(() => api.crossProjectAccess().then(setGrants).catch(() => setGrants([])), []);
  useEffect(() => { setItems(null); load(); }, [load]);
  useEffect(() => { loadGrants(); }, [loadGrants]);

  const decide = async (a, action) => {
    setBusy(true);
    try {
      const expDays = a.kind === "CROSS_PROJECT" && action === "OVERRIDE" ? expiryDays[a.id] : undefined;
      await api.decideApproval(a.id, action, t("ap.notePrefix", { action }), expDays ? Number(expDays) : undefined);
      toast.success(t("ap.recorded", { action, suffix: action === "OVERRIDE" ? t("ap.overrideSuffix") : "" }));
      await load();
      await loadGrants();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.actionFailed")); } finally { setBusy(false); }
  };

  const revoke = async (g) => {
    setBusy(true);
    try {
      await api.revokeCrossProject(g.id);
      toast.success(t("ap.revokeToast", { from: g.from_project, to: g.to_project }));
      await loadGrants();
    } catch (e) { toast.error(e.response?.data?.detail || t("ap.revokeFailed")); } finally { setBusy(false); }
  };

  const activeGrants = (grants || []).filter((g) => g.status === "ACTIVE");
  const grantHistory = (grants || []).filter((g) => ["REVOKED", "EXPIRED", "DENIED"].includes(g.status));

  return (
    <div className="space-y-6">
      <div>
        <p className="mono-label">{t("ap.label")}</p>
        <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><ShieldCheck className="text-[#2563EB]" /> {t("ap.title")}</h1>
        <p className="text-sm text-[#94A3B8] mt-1">{t("ap.subtitle")}</p>
      </div>

      {activeGrants.length > 0 && (
        <div className="m-card p-5" style={{ borderColor: "#9333EA" }} data-testid="cross-project-grants-panel">
          <div className="flex items-center gap-2 mb-1">
            <Link2Off size={16} className="text-[#F0ABFC]" />
            <p className="text-white font-semibold">{t("ap.activeGrants")}</p>
            <span className="mono-label text-[9px] ml-1">{activeGrants.length} GRANTED</span>
          </div>
          <p className="text-xs text-[#94A3B8] mb-3">{t("ap.grantsDesc")}</p>
          <div className="space-y-2">
            {activeGrants.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-3 bg-[#0B0F19] border border-[#1E293B] rounded-lg p-3" data-testid={`cross-project-grant-${g.id}`}>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{g.from_project} <span className="text-[#64748B]">→</span> {g.to_project}</p>
                  <p className="text-[11px] text-[#64748B] truncate">{g.reason || t("ap.defaultReason")}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: g.expires_at ? "#FBBF24" : "#475569" }} data-testid={`cross-project-expiry-${g.id}`}>
                    {g.expires_at ? t("ap.expirySet", { d: new Date(g.expires_at).toLocaleDateString() }) : t("ap.noExpiry")}
                  </p>
                </div>
                <button disabled={busy} onClick={() => revoke(g)} className="btn-ghost h-8 px-3 text-xs flex items-center gap-1.5 shrink-0" style={{ color: "#FCA5A5" }} data-testid={`cross-project-revoke-${g.id}`}>
                  <Link2Off size={13} /> {t("ap.revoke")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {grantHistory.length > 0 && (
        <div className="m-card p-5" data-testid="cross-project-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <History size={15} className="text-[#64748B]" />
            <p className="text-white font-semibold text-sm">{t("ap.history")}</p>
            <span className="mono-label text-[9px] ml-1">{grantHistory.length}</span>
          </div>
          <div className="space-y-1.5">
            {grantHistory.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-[#1E293B] last:border-0" data-testid={`cross-project-history-${g.id}`}>
                <span className="text-[#94A3B8] truncate">{g.from_project} → {g.to_project}</span>
                <StatusPill status={g.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 bg-[#0F1626] border border-[#1E293B] rounded-xl p-1 w-fit">
        {["PENDING", "ALL"].map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} className={`px-4 py-1.5 rounded-lg text-xs ${tab === tb ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`} data-testid={`approvals-tab-${tb}`}>{tb === "ALL" ? t("ap.tabAll") : tb}</button>
        ))}
      </div>

      {!items ? <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#2563EB]" /></div>
        : items.length === 0 ? <div className="m-card p-10 text-center text-[#64748B] text-sm">{t("ap.empty")}</div>
        : (
          <div className="grid lg:grid-cols-2 gap-4">
            {items.map((a) => {
              const isLock = a.class === "A4";
              const isPending = a.status === "PENDING";
              return (
                <div key={a.id} className="m-card p-5" style={{ borderColor: isLock ? "#9333EA" : undefined }} data-testid={`approval-card-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2"><StatusPill status={a.class} /><StatusPill status={a.status} /></div>
                    <span className="mono-label">{a.project_code}</span>
                  </div>
                  <p className="text-white font-semibold">{a.title}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{a.requested_action}</p>

                  <div className="flex items-center gap-2 mt-3 text-xs text-[#64748B]"><User size={12} /> {t("ap.requestedBy", { v: a.requesting_agent_name })}</div>

                  {isLock && <div className="mt-3 flex items-start gap-2 text-xs text-[#F0ABFC] bg-[#3B0764] border border-[#9333EA] rounded-lg p-3"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{a.impact}</span></div>}

                  {a.has_fallback && <div className="mt-3 flex items-start gap-2 text-xs text-[#FDBA74] bg-[#431407] border border-[#EA580C] rounded-lg p-3" data-testid={`approval-fallback-warning-${a.id}`}><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{t("ap.fallbackWarning")}</span></div>}

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                    {[[t("ap.risk"), a.risk], [t("ap.cost"), `$${a.cost}`], [t("ap.reversible"), a.reversibility], [t("ap.aiRec"), a.ai_recommendation]].map(([k, v]) => (
                      <div key={k} className="bg-[#0B0F19] rounded-lg p-2.5 border border-[#1E293B]"><p className="mono-label text-[9px]">{k}</p><p className="text-white mt-0.5 text-[11px]">{v}</p></div>
                    ))}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-3">{a.reason}</p>

                  {isPending && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {isLock ? (
                        <>
                          {a.kind === "CROSS_PROJECT" && (
                            <input type="number" min="1" placeholder={t("ap.expiryPlaceholder")} value={expiryDays[a.id] || ""} onChange={(e) => setExpiryDays((s) => ({ ...s, [a.id]: e.target.value }))} className="input-dark h-9 px-3 text-xs w-40" data-testid={`approval-expiry-input-${a.id}`} />
                          )}
                          <button disabled={busy} onClick={() => decide(a, "OVERRIDE")} className="btn-primary h-9 px-4 text-xs flex items-center gap-1.5" style={{ background: "#9333EA", boxShadow: "0 4px 20px rgba(147,51,234,0.3)" }} data-testid={`approval-override-button-${a.id}`}><AlertTriangle size={13} /> {t("ap.confirmOverride")}</button>
                          <button disabled={busy} onClick={() => decide(a, "REJECT")} className="btn-ghost h-9 px-4 text-xs flex items-center gap-1.5" style={{ color: "#FCA5A5" }} data-testid={`approval-reject-button-${a.id}`}><X size={13} /> {t("common.reject")}</button>
                        </>
                      ) : (
                        <>
                          <button disabled={busy || a.has_fallback} title={a.has_fallback ? t("common.retryProviderTitle") : ""} onClick={() => decide(a, "APPROVE")} className="btn-primary h-9 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40" data-testid={`approval-approve-button-${a.id}`}><Check size={13} /> {t("common.approve")}</button>
                          <button disabled={busy} onClick={() => decide(a, "REVISE")} className="btn-ghost h-9 px-4 text-xs flex items-center gap-1.5" data-testid={`approval-revise-button-${a.id}`}><RotateCcw size={13} /> {t("common.revise")}</button>
                          <button disabled={busy} onClick={() => decide(a, "REJECT")} className="btn-ghost h-9 px-4 text-xs flex items-center gap-1.5" style={{ color: "#FCA5A5" }} data-testid={`approval-reject-button-${a.id}`}><X size={13} /> {t("common.reject")}</button>
                        </>
                      )}
                      {a.mission_id && <button onClick={() => navigate(`/missions/${a.mission_id}`)} className="btn-ghost h-9 px-4 text-xs" data-testid={`approval-open-mission-${a.id}`}>{t("ap.openMission")}</button>}
                      {a.task_id && a.project_code && (
                        <button onClick={() => navigate(`/projects/${a.project_code}?tab=tasks`)}
                          className="btn-ghost h-9 px-4 text-xs" data-testid={`approval-open-task-${a.id}`}>
                          {lang === "id" ? "Buka Task" : "Open Task"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
